import { AsyncPipe, NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { ColorSchemeService } from '@examdojo/browser/color-scheme';
import { PageQuery, PageService, SyllabusQuery, TopicLevel2Query, TopicLevel2UIModel } from '@examdojo/category/v2';
import { ButtonComponent } from '@examdojo/core/button';
import { EmptyStateComponent } from '@examdojo/core/empty-state';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { LocalizedStringComponent, LocalizePipe } from '@examdojo/core/i18n';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';
import { PlatformService } from '@examdojo/platform';
import { ContentBlocksRendererComponent, ContentBlockTocComponent } from '@examdojo/study-notes';
import { IonHeader, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { RootUrlParts } from '../../../app.model';
import { PageLayoutComponent } from '../../../shared/page-layout/page-layout.component';
import { CardComponent } from '../card-link/card-link.component';
import { PAGE_SLUG_PARAM } from '../learn-hub.routes';
import { TopicSelectionPersistenceService } from '../topic-selection-persistence.service';
import { RelatedArticlesComponent } from './related-articles.component';
import { TopicBreadcrumbsComponent } from './topic-breadcrumbs.component';

const LOADING_BAR_MIN_DURATION_MS = 400;
const EMPTY_STATE_DELAY_MS = 200;

@Component({
  selector: 'dojo-topic-level-2-detail-view',
  imports: [
    TranslocoPipe,
    RouterLink,
    TopicBreadcrumbsComponent,
    LocalizedStringComponent,
    PageLayoutComponent,
    IconComponent,
    NgClass,
    LocalizePipe,
    AsyncPipe,
    MarkdownViewerComponent,
    ButtonComponent,
    IonHeader,
    IonTitle,
    IonToolbar,
    IconButtonComponent,
    ContentBlockTocComponent,
    EmptyStateComponent,
    CardComponent,
    ContentBlocksRendererComponent,
    MatProgressBarModule,
    RelatedArticlesComponent,
  ],
  templateUrl: './topic-level-2-detail-view.component.html',
  styleUrl: './topic-level-2-detail-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'relative block w-full safe-area-top safe-area-bottom' },
})
export class TopicLevel2DetailViewComponent implements AfterViewInit {
  constructor(
    private readonly topicLevel2Query: TopicLevel2Query,
    private readonly pageService: PageService,
    private readonly pageQuery: PageQuery,
    private readonly elementRef: ElementRef,
    private readonly platformService: PlatformService,
    private readonly topicSelectionPersistenceService: TopicSelectionPersistenceService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly colorSchemeService: ColorSchemeService,
    private readonly route: ActivatedRoute,
  ) {
    merge(this.fetchPagesOnTopicLevel2Change(), this.saveLastUsedTL2OnTopicChange())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }
  private readonly topic$: Observable<TopicLevel2UIModel | null> = this.topicLevel2Query.active$;
  private readonly isLoadingPages$ = new BehaviorSubject<boolean>(false);
  readonly scrollableContainer = signal<HTMLElement | undefined>(undefined);
  /** Tracks whether we've attempted to fetch pages at least once for the current topic */
  private readonly hasFetchedPages$ = new BehaviorSubject<boolean>(false);

  private readonly topicSlug$ = this.topic$.pipe(
    filter(Boolean),
    map((topic) => topic.slug),
  );

  private readonly activePage$ = combineLatest([this.pageQuery.entities$, this.route.paramMap]).pipe(
    map(([pages, params]) => {
      const pageSlug = params.get(PAGE_SLUG_PARAM);
      if (pageSlug) {
        return pages.find((p) => p.slug === pageSlug);
      }
      // Return the first page if no specific slug is provided
      return pages[0];
    }),
    startWith(undefined),
  );

  private readonly pagesCount$ = this.pageQuery.entities$.pipe(
    map((pages) => pages.length),
    startWith(0),
  );

  private readonly hasNoPages$ = this.pagesCount$.pipe(map((count) => count === 0));

  readonly showPageBreadcrumb$ = this.pagesCount$.pipe(map((count) => count > 1));

  private readonly isContentEmpty$ = this.activePage$.pipe(
    map((page) => !!page && (!page.content || page.content.length === 0)),
    startWith(false),
  );

  /**
   * Only show empty state after loading completes AND content is actually empty.
   * This prevents a flash of the empty state while content is being fetched.
   * We also wait until we've fetched pages at least once to avoid showing empty state
   * before the initial fetch completes.
   */
  private readonly showEmptyState$ = combineLatest([
    this.hasNoPages$,
    this.isContentEmpty$,
    this.isLoadingPages$,
    this.hasFetchedPages$,
  ]).pipe(
    map(
      ([hasNoPages, isContentEmpty, isLoading, hasFetched]) =>
        hasFetched && !isLoading && (hasNoPages || isContentEmpty),
    ),
    distinctUntilChanged(),
    switchMap((shouldShow) => (shouldShow ? timer(EMPTY_STATE_DELAY_MS).pipe(map(() => true)) : of(false))),
    startWith(false),
  );

  /**
   * Shows loading bar for at least LOADING_BAR_MIN_DURATION_MS.
   * When loading starts: immediately show bar
   * When loading ends: wait for minimum duration before hiding
   */
  private readonly showLoadingBar$ = this.isLoadingPages$.pipe(
    distinctUntilChanged(),
    switchMap((isLoading) => {
      if (isLoading) {
        return of(true);
      }
      // Delay hiding by minimum duration
      return of(false).pipe(delay(LOADING_BAR_MIN_DURATION_MS));
    }),
    startWith(false),
  );

  readonly state = connectState({
    topic: this.topic$,
    showLoadingBar: this.showLoadingBar$,
    isMobile: this.platformService.isSmall$,
    activePage: this.activePage$,
    showEmptyState: this.showEmptyState$,
    darkMode: this.colorSchemeService.hasDarkMode$,
    showPageBreadcrumb: this.showPageBreadcrumb$,
    formulaBookletUrl: this.syllabusQuery.activeFormularBooklet$,
  });

  protected readonly RootUrlParts = RootUrlParts;

  ngAfterViewInit() {
    const scrollableElement = this.elementRef.nativeElement.closest('.main-content-area') as HTMLElement;
    this.scrollableContainer.set(scrollableElement);
  }

  private fetchPagesOnTopicLevel2Change() {
    return this.topic$.pipe(
      filter(Boolean),
      distinctUntilKeyChanged('slug'),
      switchMap((topicLevel2) => {
        // Reset fetch state for new topic
        this.hasFetchedPages$.next(false);
        this.isLoadingPages$.next(true);
        return this.pageService.fetchForTopicLevel2(topicLevel2.slug).pipe(
          tap(() => this.hasFetchedPages$.next(true)),
          finalize(() => this.isLoadingPages$.next(false)),
          this.errorHandlerService.catchHttpErrors(() => EMPTY),
        );
      }),
    );
  }

  private saveLastUsedTL2OnTopicChange() {
    return combineLatest([this.topic$, this.route.paramMap]).pipe(
      filter(([topic]) => !!topic),
      map(([topic, params]) => ({
        topic: topic!,
        pageSlug: params.get(PAGE_SLUG_PARAM) ?? undefined,
      })),
      distinctUntilChanged((prev, curr) => prev.topic.id === curr.topic.id && prev.pageSlug === curr.pageSlug),
      tap(({ topic, pageSlug }) => {
        const syllabusSlug = topic.topicLevel1?.syllabus?.slug;
        const topicLevel1Slug = topic.topicLevel1?.slug;
        const courseLevel = this.syllabusQuery.getProp('course_level') ?? null;
        if (syllabusSlug && topicLevel1Slug) {
          this.topicSelectionPersistenceService.setLastUsedPage(
            syllabusSlug,
            courseLevel,
            topicLevel1Slug,
            topic.slug,
            pageSlug,
          );
        }
      }),
    );
  }
}
